import React, { useContext, useState } from 'react';
import { ClientSideDataGrid } from 'src/components/CaseListTable';
import {
  IGetUsersForAccountResponse,
  IRemoveUserFromAccountRequest,
  IdentityTypes,
  UserDetails,
} from 'src/state/slice/models/CaseLifecycleTypes';
import { getColumnsConfig } from './config/TableConfiguration';
import { LIST_USER_PAGE } from 'src/self-service/constants/SelfServiceConstants';
import { UMS_ACCOUNT_ENTITY_CLASS } from 'src/constants/GlobalConstants';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { useRemoveUserFromAccountMutation } from 'src/state/slice/umsServiceApi';
import { fetchAPIErrorMessage } from 'src/state/slice/utils/apiUtils';
import { PUB_SUB_EVENT, usePub } from 'src/hooks/usePubSub';
import { Toast } from '@amzn/katal-components/es/toast-group';

export interface IListUserTable {
  data: IGetUsersForAccountResponse | undefined;
  accountId: string;
}
export const ListUserTable: React.FC<IListUserTable> = ({
  data,
  accountId,
}) => {
  if (!data) {
    return <>{LIST_USER_PAGE.LIST_USER_NO_DATA_ERROR_RESPONSE}</>;
  }
  const publish = usePub();
  const { clientId } = useContext(AuthContext);
  const [removeUserFromAccount, { isError, error, isSuccess }] =
    useRemoveUserFromAccountMutation();

  const onDeleteUser = (userDetails: UserDetails) => {
    const payload: IRemoveUserFromAccountRequest = {
      user: {
        userId: userDetails.userId,
        attributes: {
          role: userDetails.role,
        },
        type: userDetails.isInternalUser
          ? IdentityTypes.INTERNAL_USER_ALIAS
          : IdentityTypes.RETAIL_CUSTOMER_ID,
      },
      account: {
        accountId: accountId,
        accountType: UMS_ACCOUNT_ENTITY_CLASS,
      },
      clientId: clientId,
      identity: { userId: '' },
    };
    removeUserFromAccount(payload);
  };

  if (isError) {
    const message = fetchAPIErrorMessage(
      error,
      LIST_USER_PAGE.REMOVE_USER_GENERIC_ERROR_MESSAGE,
    );
    publish(PUB_SUB_EVENT.SEND_GLOBAL_MESSAGE, {
      id: message.length.toString(),
      description: message,
      variant: 'danger',
      persistent: false,
    });
  }

  if (isSuccess) {
    const message = LIST_USER_PAGE.REMOVE_USER_SUCCESS_MESSAGE;
    const messageToast: Toast = {
      id: message.length.toString(),
      description: message,
      variant: 'success',
      persistent: false,
    };
    publish(PUB_SUB_EVENT.SEND_GLOBAL_MESSAGE, messageToast);
  }

  return (
    <>
      <span className="text-secondary">
        {LIST_USER_PAGE.LIST_USER_TABLE_HEADER}
      </span>
      <hr />
      <ClientSideDataGrid
        items={data?.userDetailsList.map((user: UserDetails) => {
          return {
            userId: user.userId,
            fullName: user.fullName,
            primaryEmail: user.primaryEmail,
            isInternalUser: user.isInternalUser,
            role: user.role,
          };
        })}
        columns={getColumnsConfig(onDeleteUser)}
        pagination={true}
        search={true}
        noResultsMessage={LIST_USER_PAGE.NO_RESULTS_MESSAGE}
      />
    </>
  );
};
